import { format, isToday, isTomorrow } from 'date-fns';

import { Box, FilterChip } from '@zillow/constellation-10';

import { dayChipRowStyles } from './styles';
import { ToursSummaryDayChipType } from './types';
import type { UpcomingToursDay } from '../../hooks/useUpcomingToursData';

type DayChipsProps = {
  days: Array<UpcomingToursDay>;
  selectedIndex: number;
  onSelect: (dayIndex: number, chip: ToursSummaryDayChipType) => void;
};

// Labeled from the date itself (not the array index) so the labels stay right if the window drifts.
const getDayChipLabel = (date: Date) => {
  if (isToday(date)) {
    return 'Today';
  }
  if (isTomorrow(date)) {
    return 'Tomorrow';
  }
  return format(date, 'EEEE');
};

const getDayChipType = (date: Date): ToursSummaryDayChipType => {
  if (isToday(date)) {
    return ToursSummaryDayChipType.Today;
  }
  if (isTomorrow(date)) {
    return ToursSummaryDayChipType.Tomorrow;
  }
  return ToursSummaryDayChipType.DayAfterTomorrow;
};

const DayChips = ({ days, selectedIndex, onSelect }: DayChipsProps) => (
  <Box css={dayChipRowStyles}>
    {days.map((day, dayIndex) => {
      // Resolved in the same render pass as the label, so a click across midnight reports the
      // identity the user actually saw rather than one recomputed from the clock at click time.
      const chipType = getDayChipType(day.date);

      return (
        <FilterChip
          key={day.date.toDateString()}
          selected={dayIndex === selectedIndex}
          onSelectedChange={(selected) => selected && onSelect(dayIndex, chipType)}
          data-testid={`ToursSummary-dayChip-${dayIndex}`}
        >
          {getDayChipLabel(day.date)}
        </FilterChip>
      );
    })}
  </Box>
);

export default DayChips;
